import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { assessmentQuestions } from '../data/questions';
import DetailedResults from './DetailedResults';

const { FiRefreshCw, FiTrendingUp, FiCheckCircle, FiTarget, FiUser, FiMail, FiBookOpen, FiDatabase, FiWifi } = FiIcons;

const Results = ({ data }) => {
  const navigate = useNavigate();
  const [showDetailedGuide, setShowDetailedGuide] = useState(false);

  const getCoachingLevel = (totalScore) => {
    if (totalScore >= 41) return { level: 'High', color: 'red', description: 'Significant coaching need identified - You\'re an ideal candidate for the Bridge Framework™' };
    if (totalScore >= 28) return { level: 'Moderate', color: 'orange', description: 'Moderate coaching support recommended - You would benefit significantly from systematic approach' };
    if (totalScore >= 14) return { level: 'Selective', color: 'primary', description: 'Selective coaching could be beneficial - Specific phases could enhance your strong foundation' };
    return { level: 'Minimal', color: 'sage', description: 'Minimal coaching need - You have exceptional self-management skills' };
  };

  const getRecommendations = (sectionKey, score) => {
    const recommendations = {
      foundation: {
        high: "Focus on building consistent daily habits and accountability systems. Start with small, achievable commitments.",
        moderate: "Strengthen goal clarity and tracking systems. Consider working with an accountability partner.",
        mild: "Fine-tune your goal-setting process and celebrate small wins to maintain momentum.",
        low: "Continue your excellent foundation practices and consider mentoring others."
      },
      clarity: {
        high: "Prioritize ruthless elimination of non-essential activities. Create a simplified, distraction-free environment.",
        moderate: "Practice saying 'no' more often and organize your environment to support your goals.",
        mild: "Regular review of commitments and minor environment optimizations will help.",
        low: "Your clarity and focus are strong assets. Use them to tackle bigger challenges."
      },
      action: {
        high: "Break everything into the smallest possible daily actions. Focus on consistency over intensity.",
        moderate: "Improve your action-taking speed and create better systems for daily progress.",
        mild: "Work on maintaining momentum during low-motivation periods.",
        low: "Your execution skills are excellent. Consider teaching others your methods."
      },
      support: {
        high: "Actively seek accountability partnerships and put yourself in growth-challenging situations.",
        moderate: "Gradually increase your comfort with external accountability and feedback.",
        mild: "Explore low-stakes accountability options to supplement your independent approach.",
        low: "Your independence is strong. Consider how you might support others' growth."
      },
      optimization: {
        high: "Start with basic tracking and measurement systems. Begin with one key metric.",
        moderate: "Expand your data collection and analysis. Consider meaningful stakes for important goals.",
        mild: "Refine your tracking systems and experiment with small accountability measures.",
        low: "Your optimization approach is sophisticated. Share your methods with others."
      }
    };

    const level = score >= 18 ? 'high' : score >= 12 ? 'moderate' : score >= 6 ? 'mild' : 'low';
    return recommendations[sectionKey]?.[level] || "Continue developing in this area.";
  };

  const getColorClasses = (color) => {
    const colorMap = {
      red: { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' },
      orange: { bg: 'bg-accent/10', text: 'text-accent', bar: 'bg-accent' },
      primary: { bg: 'bg-primary-100', text: 'text-primary-700', bar: 'bg-primary-500' },
      sage: { bg: 'bg-sage-100', text: 'text-sage-700', bar: 'bg-sage-500' }
    };
    return colorMap[color] || colorMap.primary;
  };

  const overallResult = getCoachingLevel(data.totalScore);

  // Database Status Indicator
  const DatabaseStatus = () => (
    <div className="mb-4">
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
        data.savedToDatabase 
          ? 'bg-sage-100 text-sage-700 border border-sage-200' 
          : 'bg-accent/10 text-accent border border-accent/20'
      }`}>
        <SafeIcon 
          icon={data.savedToDatabase ? FiDatabase : FiWifi} 
          className="mr-2 text-xs" 
        />
        {data.savedToDatabase ? 'Results saved securely' : 'Results stored locally'}
      </div>
      {data.savedToDatabase && data.databaseId && (
        <p className="text-xs text-text-light mt-1">
          Assessment ID: {data.databaseId.slice(-8)}
        </p>
      )}
    </div>
  );

  if (showDetailedGuide) {
    return (
      <div>
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => setShowDetailedGuide(false)}
            className="flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 transition-colors"
          >
            ← Back to Summary
          </button>
          <h2 className="text-2xl font-bold text-text-primary">
            Bridge Framework™ Results Guide
          </h2>
        </div>
        <DetailedResults data={data} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto space-y-8"
    >
      {/* User Info Header */}
      <div className="bg-bg-primary rounded-2xl ernest-shadow p-6">
        <DatabaseStatus />
        <div className="flex items-center justify-center space-x-4">
          <div className="ernest-gradient p-4 rounded-xl ernest-hover">
            <SafeIcon icon={FiUser} className="text-white text-xl" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-text-primary">{data.userInfo.name}</h3>
            <p className="text-text-secondary flex items-center justify-center">
              <SafeIcon icon={FiMail} className="mr-2 text-sm" />
              {data.userInfo.email}
            </p>
            <p className="text-sm text-text-light mt-1">
              Assessment completed on {new Date(data.completedAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Overall Results */}
      <div className="bg-bg-primary rounded-2xl ernest-shadow p-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${getColorClasses(overallResult.color).bg} border-2 border-current`}>
            <SafeIcon 
              icon={overallResult.color === 'sage' ? FiCheckCircle : FiTarget} 
              className={`text-3xl ${getColorClasses(overallResult.color).text}`} 
            />
          </div>
          <h2 className="text-3xl font-bold text-text-primary mb-2">
            Your Assessment Results
          </h2>
          <p className="text-lg text-text-secondary mb-6">
            Overall Coaching Need: <span className={`font-semibold ${getColorClasses(overallResult.color).text}`}>{overallResult.level}</span>
          </p>
          <div className="bg-gradient-to-r from-bg-light to-bg-cream rounded-xl p-6 border border-gray-1">
            <div className="text-4xl font-bold text-text-primary mb-2">
              {data.totalScore} / 54
            </div>
            <div className="text-text-secondary">
              {overallResult.description}
            </div>
          </div>
        </div>
      </div>

      {/* Section Breakdown */}
      <div className="bg-bg-primary rounded-2xl ernest-shadow p-8">
        <h3 className="text-2xl font-bold text-text-primary mb-6 flex items-center">
          <SafeIcon icon={FiTrendingUp} className="mr-3 text-primary-600" />
          Detailed Analysis by Section
        </h3>

        <div className="space-y-6">
          {Object.entries(data.sectionScores).map(([sectionKey, sectionData]) => {
            const sectionResult = getCoachingLevel(sectionData.score);
            const sectionInfo = assessmentQuestions[sectionKey];
            const colorClasses = getColorClasses(sectionResult.color);

            return (
              <motion.div
                key={sectionKey}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="border-2 border-gray-1 rounded-xl p-6 hover:border-sage-300 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-text-primary">
                    {sectionInfo.title}
                  </h4>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium border-2 ${colorClasses.bg} ${colorClasses.text} border-current`}>
                    {sectionResult.level} Need
                  </span>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm text-text-secondary mb-2">
                    <span>Score: {sectionData.score} / {sectionData.maxScore}</span>
                    <span>{Math.round((sectionData.score / sectionData.maxScore) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-1 rounded-full h-3">
                    <motion.div
                      className={`h-3 rounded-full ${colorClasses.bar}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${(sectionData.score / sectionData.maxScore) * 100}%` }}
                      transition={{ duration: 1, delay: 0.2 }}
                    />
                  </div>
                </div>

                <div className="bg-bg-light rounded-lg p-4 border border-gray-1">
                  <h5 className="font-medium text-text-primary mb-2 flex items-center">
                    <SafeIcon icon={FiTarget} className="mr-2 text-text-secondary" />
                    Recommendation
                  </h5>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {getRecommendations(sectionKey, sectionData.score)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Detailed Guide CTA */}
      <div className="bg-accent rounded-2xl ernest-shadow p-8 text-white">
        <div className="text-center">
          <SafeIcon icon={FiBookOpen} className="text-4xl mb-4 mx-auto" />
          <h3 className="text-2xl font-bold mb-4">
            Get Your Complete Bridge Framework™ Results Guide
          </h3>
          <p className="text-accent-light opacity-90 mb-6 leading-relaxed max-w-2xl mx-auto">
            Unlock detailed analysis, personalized action plans, phase-by-phase recommendations, 
            and exclusive resources tailored to your specific assessment results.
          </p>
          <button
            onClick={() => setShowDetailedGuide(true)}
            className="inline-flex items-center px-8 py-4 bg-bg-primary text-accent rounded-xl font-semibold text-lg hover:bg-bg-light transition-all transform hover:scale-105 ernest-shadow"
          >
            <SafeIcon icon={FiBookOpen} className="mr-3" />
            View Complete Results Guide
          </button>
        </div>
      </div>

      {/* Booking CTA */}
      <div className="ernest-gradient rounded-2xl ernest-shadow p-8 text-white">
        <h3 className="text-2xl font-bold mb-6">Ready to tackle those challenges head-on?</h3>
        <p className="text-primary-100 mb-6 leading-relaxed">
          Ready to tackle those challenges head-on? If your assessment highlighted areas where you're struggling—or 
          you're simply excited to accelerate your growth—Coach Ernest is here to help you create your personalized 
          roadmap forward through a Bridge Framework strategy session.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center px-6 py-3 bg-bg-primary text-primary-600 rounded-xl font-semibold hover:bg-bg-light transition-all ernest-hover"
          >
            <SafeIcon icon={FiRefreshCw} className="mr-2" />
            Take Assessment Again
          </button>
          <a
            href="https://www.coachedbyernest.com/booking-calendar/initial-discovery-session-90-mins?utm_medium=page_links"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center px-6 py-3 bg-primary-700 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all ernest-hover"
          >
            Schedule Your Initial Discovery Session
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default Results;